import { useEffect, useState } from "react";
import { Plus, Power, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

export default function Localities() {
  const [cities, setCities] = useState([]);
  const [localities, setLocalities] = useState([]);
  const [cityFilter, setCityFilter] = useState("");
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ city_id: "", name: "" });

  const load = () => api.get("/localities").then(({ data }) => setLocalities(data));
  useEffect(() => { api.get("/cities").then(({ data }) => setCities(data)); load(); }, []);

  const openAdd = () => { setEditId(null); setForm({ city_id: "", name: "" }); setOpen(true); };
  const openEdit = (l) => { setEditId(l.id); setForm({ city_id: l.city_id, name: l.name, active: l.active }); setOpen(true); };

  const save = async () => {
    if (!editId && (!form.city_id || !form.name)) { toast.error("City and locality name required"); return; }
    if (editId && !form.name) { toast.error("Locality name required"); return; }
    try {
      if (editId) { await api.put(`/admin/localities/${editId}`, { name: form.name, active: form.active ?? true }); toast.success("Locality updated"); }
      else { await api.post("/admin/localities", { ...form, active: true }); toast.success("Locality added"); }
      setOpen(false); setForm({ city_id: "", name: "" }); setEditId(null); load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const toggle = async (id) => { await api.patch(`/admin/localities/${id}/toggle`); load(); };
  const remove = async (id) => { if (!window.confirm("Delete this locality?")) return; try { await api.delete(`/admin/localities/${id}`); toast.success("Locality deleted"); load(); } catch (e) { toast.error(apiError(e)); } };

  const filtered = localities.filter((l) => (!cityFilter || l.city === cityFilter) && l.name.toLowerCase().includes(search.toLowerCase()));

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div><h1 className="font-heading text-2xl font-extrabold">Localities</h1><p className="text-muted-foreground text-sm font-medium">Internal stock-location data. Buyers never select these.</p></div>
        <Button data-testid="add-locality-btn" onClick={openAdd} className="font-bold gap-1"><Plus className="h-4 w-4" /> Add Locality</Button>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent>
            <DialogHeader><DialogTitle>{editId ? "Edit Locality" : "Add Locality"}</DialogTitle></DialogHeader>
            <div className="space-y-3">
              {!editId && (
                <div><Label>City</Label>
                  <Select value={form.city_id} onValueChange={(v) => setForm({ ...form, city_id: v })}>
                    <SelectTrigger data-testid="locality-city"><SelectValue placeholder="Select city" /></SelectTrigger>
                    <SelectContent>{cities.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              )}
              <div><Label>Locality Name</Label><Input data-testid="locality-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
              <Button data-testid="save-locality" onClick={save} className="w-full font-bold">{editId ? "Update Locality" : "Save Locality"}</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="flex flex-wrap gap-2">
        <Input data-testid="locality-search" placeholder="Search locality…" value={search} onChange={(e) => setSearch(e.target.value)} className="max-w-xs" />
        <Select value={cityFilter || "all"} onValueChange={(v) => setCityFilter(v === "all" ? "" : v)}>
          <SelectTrigger data-testid="locality-city-filter" className="w-auto min-w-[150px]"><SelectValue placeholder="City" /></SelectTrigger>
          <SelectContent><SelectItem value="all">All Cities</SelectItem>{cities.map((c) => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      <div className="bg-card border rounded-xl overflow-x-auto ct-scroll">
        <Table>
          <TableHeader><TableRow><TableHead>Locality</TableHead><TableHead>City</TableHead><TableHead>Status</TableHead><TableHead>Actions</TableHead></TableRow></TableHeader>
          <TableBody>
            {filtered.map((l) => (
              <TableRow key={l.id} data-testid={`locality-row-${l.id}`}>
                <TableCell className="font-semibold">{l.name}</TableCell>
                <TableCell>{l.city}</TableCell>
                <TableCell><Badge className={`border ${l.active ? "bg-emerald-100 text-emerald-800 border-emerald-300" : "bg-slate-200 text-slate-600"}`}>{l.active ? "Active" : "Inactive"}</Badge></TableCell>
                <TableCell>
                  <div className="flex gap-1">
                    <Button size="sm" variant="outline" data-testid={`edit-locality-${l.id}`} onClick={() => openEdit(l)}><Pencil className="h-4 w-4" /></Button>
                    <Button size="sm" variant="outline" data-testid={`toggle-locality-${l.id}`} onClick={() => toggle(l.id)}><Power className={`h-4 w-4 ${l.active ? "text-rose-600" : "text-emerald-600"}`} /></Button>
                    <Button size="sm" variant="outline" data-testid={`delete-locality-${l.id}`} onClick={() => remove(l.id)}><Trash2 className="h-4 w-4 text-rose-600" /></Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {filtered.length === 0 && <TableRow><TableCell colSpan={4} className="text-center py-10 text-muted-foreground font-semibold">No localities.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
